import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { CreatedContent, Message as MessageType, SearchResultItem } from './types';
import { Context as AISearchContext } from './context';
import {
  MessageRow,
  MessageAvatar,
  MessageBubble,
  MessageContent,
  MessageTimestamp,
  LinksContainer,
  MessageLink,
  ActionsContainer,
  ActionButton,
  SearchResultsContainer,
  SearchResultCard,
  SearchResultHeader,
  ResultThumbnail,
  ReasoningBlock,
  ReasoningStep,
  ToolChips,
  ToolChip,
  SearchResultTitle,
  SearchResultScore,
  ScoreBar,
  ScoreFill,
  ScoreLabel,
  SearchResultMeta,
  ContentTypeBadge,
  SourceBadge,
  MatchReason,
  TagsRow,
  TagChip,
  SearchResultActions,
  SmallActionButton,
  CreatedCard,
  CreatedHeader,
  CreatedBadge,
  CreatedTitle,
  CreatedSources,
  CreatedSourcesLabel,
  CreatedOpenLink
} from './styles';
import { AI_ICON } from './constants';

interface MessageProps {
  message: MessageType;
  isNew?: boolean;
  onActionClick?: (actionId: string, payload: any) => void;
}

const formatTime = (timestamp: Date | string) => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${displayHours}:${displayMinutes} ${ampm}`;
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Human wording for the tools the assistant ran. */
const TOOL_LABELS: Record<string, string> = {
  search_content: 'Searched content',
  find_related_articles: 'Found related articles',
  get_article: 'Read the article',
  create_article: 'Created an article',
  create_brief: 'Created a brief',
  update_article: 'Edited the draft',
  append_article_correction: 'Attached a correction',
  tag_article: 'Added tags',
  add_related_articles: 'Linked related content',
  update_article_seo: 'Updated SEO metadata',
  get_staleness_report: 'Read the staleness report',
  set_lifecycle_state: 'Updated the lifecycle flags',
};

const toolLabel = (tool: string) =>
  TOOL_LABELS[tool] || tool.replace(/_/g, ' ');

const reasoningSummary = (message: MessageType) => {
  const steps = message.reasoning?.length || 0;
  const tools = message.toolsUsed?.length || 0;
  if (steps && tools) return `How I worked this out (${steps} step${steps === 1 ? '' : 's'}, ${tools} action${tools === 1 ? '' : 's'})`;
  if (steps) return `How I worked this out (${steps} step${steps === 1 ? '' : 's'})`;
  return `What I did (${tools} action${tools === 1 ? '' : 's'})`;
};

const sourceLabel = (source: string) => {
  switch (source) {
    case 'semantic': return 'AI Match';
    case 'keyword': return 'Keyword';
    case 'both': return 'AI + Keyword';
    default: return source;
  }
};

const SearchResult: React.FC<{
  result: SearchResultItem;
  onActionClick?: (actionId: string, payload: any) => void;
}> = ({ result, onActionClick }) => {
  const { pageContext } = useContext(AISearchContext);
  const hasEditor = pageContext?.pageType === 'article-edit' || pageContext?.pageType === 'page-edit';

  return (
    <SearchResultCard>
      <SearchResultHeader>
        {/* Editors recognize the featured image before the headline */}
        {result.imageUrl && (
          <ResultThumbnail src={result.imageUrl} alt="" aria-hidden="true" />
        )}
        <SearchResultTitle>
          <Link to={result.url} style={{ color: 'inherit', textDecoration: 'none' }}>
            {result.title}
          </Link>
        </SearchResultTitle>
        <SearchResultScore>
          <ScoreBar>
            <ScoreFill $score={result.score} />
          </ScoreBar>
          <ScoreLabel>{Math.round(result.score * 100)}%</ScoreLabel>
        </SearchResultScore>
      </SearchResultHeader>

      <SearchResultMeta>
        <ContentTypeBadge $type={result.contentType}>{result.contentType}</ContentTypeBadge>
        <SourceBadge $source={result.source}>{sourceLabel(result.source)}</SourceBadge>
        <span>{formatDate(result.publishedAt)}</span>
        {result.authors.length > 0 && (
          <span>by {result.authors.join(', ')}</span>
        )}
      </SearchResultMeta>

      <MatchReason>{result.matchReason}</MatchReason>

      {(result.taxonomies.length > 0 || result.keywords.length > 0) && (
        <TagsRow>
          {result.taxonomies.map((tax, i) => (
            <TagChip key={`tax-${i}`}>{tax}</TagChip>
          ))}
          {result.keywords.map((kw, i) => (
            <TagChip key={`kw-${i}`}>{kw}</TagChip>
          ))}
        </TagsRow>
      )}

      <SearchResultActions>
        <SmallActionButton
          onClick={() => onActionClick?.('insert-link', {
            type: 'insert-link',
            title: result.title,
            url: result.canonicalUrl || result.url,
            resultId: result.id
          })}
          title={hasEditor ? 'Insert link at cursor position in editor' : 'Copy link URL to clipboard'}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {hasEditor ? 'Insert Link' : 'Copy Link'}
        </SmallActionButton>
        <SmallActionButton
          onClick={() => onActionClick?.('add-related', {
            type: 'add-related',
            title: result.title,
            url: result.url,
            resultId: result.id,
            contentType: result.contentType
          })}
          title="Add as related content"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Add to Related
        </SmallActionButton>
        <SmallActionButton
          onClick={() => {
            if (navigator.clipboard) {
              const url = result.canonicalUrl || result.url;
              navigator.clipboard.writeText(/^https?:/.test(url) ? url : window.location.origin + url);
            }
          }}
          title="Copy URL"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Copy URL
        </SmallActionButton>
      </SearchResultActions>
    </SearchResultCard>
  );
};

/**
 * Something the assistant created. Its own card rather than a line in the
 * link list, so the editor can see at a glance that a draft now exists, what
 * it was written from, and where to open it.
 */
export const CreatedItem: React.FC<{ item: CreatedContent }> = ({ item }) => (
  <CreatedCard>
    <CreatedHeader>
      <CreatedBadge>{item.kind === 'brief' ? 'Brief' : 'Article'}</CreatedBadge>
      <span>{item.published ? 'Published' : 'Unpublished draft'}</span>
    </CreatedHeader>
    <CreatedTitle>{item.title}</CreatedTitle>
    {item.sources.length > 0 && (
      <CreatedSources>
        <CreatedSourcesLabel>
          From {item.sources.length} article{item.sources.length === 1 ? '' : 's'}
        </CreatedSourcesLabel>
        <ul>
          {item.sources.map((source, i) => (
            <li key={`${i}-${source.url}`}>
              <Link to={source.url}>{source.text}</Link>
            </li>
          ))}
        </ul>
      </CreatedSources>
    )}
    <CreatedOpenLink to={item.url}>
      Open {item.kind === 'brief' ? 'brief' : 'draft'}
    </CreatedOpenLink>
  </CreatedCard>
);

export const Message: React.FC<MessageProps> = ({ message, isNew, onActionClick }) => {
  return (
    <MessageRow $role={message.role}>
      {message.role === 'assistant' && (
        <MessageAvatar src={AI_ICON} alt="AI" />
      )}
      <MessageBubble $role={message.role}>
        {(message.reasoning?.length || message.toolsUsed?.length) ? (
          <ReasoningBlock>
            <summary>
              {reasoningSummary(message)}
            </summary>
            {message.reasoning?.map((step, i) => (
              <ReasoningStep key={`step-${i}`}>{step}</ReasoningStep>
            ))}
            {message.toolsUsed && message.toolsUsed.length > 0 && (
              <ToolChips>
                {message.toolsUsed.map((tool, i) => (
                  <ToolChip key={`tool-${i}`}>{toolLabel(tool)}</ToolChip>
                ))}
              </ToolChips>
            )}
          </ReasoningBlock>
        ) : null}

        <MessageContent>
          {message.content}
        </MessageContent>

        {message.created?.map((item, i) => (
          <CreatedItem key={`${i}-${item.oid}`} item={item} />
        ))}

        {message.searchResults && message.searchResults.length > 0 && (
          <SearchResultsContainer>
            {message.searchResults.map((result) => (
              <SearchResult
                key={result.id}
                result={result}
                onActionClick={onActionClick}
              />
            ))}
          </SearchResultsContainer>
        )}

        {message.links && message.links.length > 0 && (
          <LinksContainer>
            {message.links.map((link, index) => (
              <Link
                key={index}
                to={link.url}
                style={{ textDecoration: 'none' }}
              >
                <MessageLink $inUserMessage={message.role === 'user'}>
                  {link.text}
                </MessageLink>
              </Link>
            ))}
          </LinksContainer>
        )}

        {message.actions && message.actions.length > 0 && (
          <ActionsContainer>
            {message.actions.map((action) => (
              <ActionButton
                key={action.id}
                onClick={() => onActionClick?.(action.id, action.payload)}
              >
                {action.label}
              </ActionButton>
            ))}
          </ActionsContainer>
        )}

        <MessageTimestamp $role={message.role}>
          {formatTime(message.timestamp)}
        </MessageTimestamp>
      </MessageBubble>
    </MessageRow>
  );
};
